import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getApgarRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface NewbornApgarRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  isViewOnly: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
}

interface NewbornApgarItem {
  label: string;
  value: string | undefined;
  score: number | undefined;
  showOther?: boolean;
  otherValue?: string | undefined;
}

export default function NewbornApgarRow({
  time,
  observation,
  showNumbersOnly,
  isViewOnly,
  showPreviousVersions = true,
  onRefresh,
}: NewbornApgarRowProps) {
  const {
    type,
    typeOther,
    heartRate,
    respiratoryEffort,
    reflexStimuli,
    muscleTone,
    color,
    notes,
    totalScore,
    hasAnswers,
    hasNotes,
  } = useMemo(
    () =>
      observation
        ? getApgarRowValues(observation)
        : {
            type: undefined,
            typeOther: undefined,
            heartRate: undefined,
            respiratoryEffort: undefined,
            reflexStimuli: undefined,
            muscleTone: undefined,
            color: undefined,
            notes: undefined,
            totalScore: undefined,
            hasAnswers: false,
            hasNotes: false,
          },
    [observation],
  );

  const apgarItems = useMemo<NewbornApgarItem[]>(
    () => [
      {
        label: "Type",
        value: type?.display,
        score: undefined,
        showOther: true,
        otherValue: typeOther,
      },
      { label: "Heart Rate", value: heartRate?.display, score: heartRate?.valueInteger },
      {
        label: "Respiratory Effort",
        value: respiratoryEffort?.display,
        score: respiratoryEffort?.valueInteger,
      },
      {
        label: "Reflex Stimuli",
        value: reflexStimuli?.display,
        score: reflexStimuli?.valueInteger,
      },
      { label: "Muscle Tone", value: muscleTone?.display, score: muscleTone?.valueInteger },
      { label: "Color", value: color?.display, score: color?.valueInteger },
    ],
    [type, typeOther, heartRate, respiratoryEffort, reflexStimuli, muscleTone, color],
  );

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">APGAR:</TextWrapper>
            <List fz="sm" withPadding>
              {apgarItems.map(
                (item, index) =>
                  (item.value || (item.showOther && item.otherValue)) && (
                    <List.Item key={`${item.label}-${item.value || item.otherValue}`}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}:
                        </TextWrapper>
                        <TextWrapper size="sm">{item.value}</TextWrapper>
                        {item.score !== undefined && (
                          <TextWrapper size="sm" variant="bold">
                            ({item.score})
                          </TextWrapper>
                        )}
                        {item.showOther && item.otherValue && (
                          <TextWrapper size="sm">- {item.otherValue}</TextWrapper>
                        )}
                      </Group>
                    </List.Item>
                  ),
              )}
              {totalScore !== undefined && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Total Score:
                    </TextWrapper>
                    <TextWrapper size="sm" variant="bold">
                      {totalScore}
                    </TextWrapper>
                  </Group>
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            APGAR Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={NewbornApgarRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
